import type { ChangeEvent, DragEvent, RefObject } from "react";
import {
  AlertCircle,
  ArrowLeft,
  Camera,
  CheckCircle2,
  Loader2,
  RotateCcw,
  Square,
  UploadCloud,
  Video,
  X,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { formatFileSize } from "../../../../utils/formatFileSize";
import { MAX_RECORDING_SECONDS } from "@/modules/candidate/constants/candidateApplication.constants";
import type { VideoTabOption } from "@/modules/candidate/types/candidateApplication.types";

interface VideoUploadStepProps {
  videoFileInputRef: RefObject<HTMLInputElement>;
  liveVideoRef: RefObject<HTMLVideoElement>;
  videoTab: VideoTabOption;
  selectedVideo: File | null;
  videoPreviewUrl: string | null;
  isVideoDragging: boolean;
  videoError: string | null;
  isCameraActive: boolean;
  cameraError: string | null;
  isRecording: boolean;
  recordingSeconds: number;
  isSubmitting: boolean;
  onTabChange: (tab: VideoTabOption) => void;
  onFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onDragOver: (event: DragEvent<HTMLDivElement>) => void;
  onDragLeave: (event: DragEvent<HTMLDivElement>) => void;
  onDrop: (event: DragEvent<HTMLDivElement>) => void;
  onRemoveVideo: () => void;
  onStartCamera: () => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onRetake: () => void;
  formatRecordingTime: (seconds: number) => string;
  onBack: () => void;
  onSubmit: () => void;
}

export default function VideoUploadStep({
  videoFileInputRef,
  liveVideoRef,
  videoTab,
  selectedVideo,
  videoPreviewUrl,
  isVideoDragging,
  videoError,
  isCameraActive,
  cameraError,
  isRecording,
  recordingSeconds,
  isSubmitting,
  onTabChange,
  onFileChange,
  onDragOver,
  onDragLeave,
  onDrop,
  onRemoveVideo,
  onStartCamera,
  onStartRecording,
  onStopRecording,
  onRetake,
  formatRecordingTime,
  onBack,
  onSubmit,
}: VideoUploadStepProps) {
  return (
    <div className="px-6 py-7">
      <div className="mx-auto max-w-2xl">
        <div className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-50">
            <Video className="h-7 w-7 text-blue-600" />
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Add a Video Introduction</h2>
          <p className="mt-2 text-sm leading-6 text-gray-500">
            Upload a video file or record one directly using your camera. Keep it short and to the point — max{" "}
            {MAX_RECORDING_SECONDS / 60} minutes.
          </p>
        </div>

        <div className="mx-auto mt-6 flex w-full max-w-xs rounded-lg border border-gray-200 bg-gray-50 p-1">
          <button
            type="button"
            onClick={() => onTabChange("upload")}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              videoTab === "upload" ? "bg-white text-blue-700 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Upload Video
          </button>
          <button
            type="button"
            onClick={() => onTabChange("record")}
            className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
              videoTab === "record" ? "bg-white text-blue-700 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            Record Video
          </button>
        </div>

        {videoTab === "upload" && (
          <>
            <input
              ref={videoFileInputRef}
              type="file"
              accept=".mp4,.webm,.mov,.avi,video/*"
              className="hidden"
              onChange={onFileChange}
            />

            {!selectedVideo ? (
              <div
                role="button"
                tabIndex={0}
                onClick={() => videoFileInputRef.current?.click()}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    videoFileInputRef.current?.click();
                  }
                }}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                className={`mt-6 cursor-pointer rounded-2xl border-2 border-dashed px-6 py-12 text-center transition-colors ${
                  isVideoDragging ? "border-blue-500 bg-blue-50" : "border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50/50"
                }`}
              >
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white shadow-sm">
                  <UploadCloud className="h-7 w-7 text-blue-600" />
                </div>
                <p className="mt-4 font-semibold text-gray-800">Drag and drop your video here</p>
                <p className="mt-1 text-sm text-gray-500">or click to browse from your device</p>
                <Button
                  type="button"
                  variant="outline"
                  className="mt-5"
                  onClick={(event) => {
                    event.stopPropagation();
                    videoFileInputRef.current?.click();
                  }}
                >
                  Select Video
                </Button>
                <p className="mt-4 text-xs text-gray-400">
                  MP4, WEBM, MOV or AVI • Maximum file size 50 MB • Maximum duration 2 minutes
                </p>
              </div>
            ) : (
              <div className="mt-6 rounded-2xl border border-blue-200 bg-blue-50/60 p-5">
                {videoPreviewUrl && <video src={videoPreviewUrl} controls className="w-full rounded-xl bg-black" />}
                <div className="mt-4 flex items-center gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white">
                    <Video className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-gray-900">{selectedVideo.name}</p>
                    <p className="mt-1 text-sm text-gray-500">{formatFileSize(selectedVideo.size)}</p>
                  </div>
                  <Button type="button" variant="ghost" size="icon" onClick={onRemoveVideo} aria-label="Remove video">
                    <X className="h-5 w-5 text-gray-500" />
                  </Button>
                </div>
                <div className="mt-4 flex items-center gap-2 text-sm font-medium text-green-700">
                  <CheckCircle2 className="h-4 w-4" />
                  Video is ready for submission
                </div>
              </div>
            )}
          </>
        )}

        {videoTab === "record" && (
          <div className="mt-6">
            {selectedVideo && videoPreviewUrl ? (
              <div className="rounded-2xl border border-blue-200 bg-blue-50/60 p-5">
                <video src={videoPreviewUrl} controls className="w-full rounded-xl bg-black" />
                <div className="mt-4 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-green-700">
                    <CheckCircle2 className="h-4 w-4" />
                    Recording ready for submission
                  </div>
                  <Button type="button" variant="outline" onClick={onRetake}>
                    <RotateCcw className="mr-2 h-4 w-4" />
                    Retake
                  </Button>
                </div>
              </div>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-gray-200 bg-gray-900">
                <div className="relative aspect-video w-full bg-black">
                  <video ref={liveVideoRef} muted playsInline className="h-full w-full object-cover" />

                  {!isCameraActive && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center text-white">
                      <Camera className="h-10 w-10 text-white/70" />
                      <p className="max-w-xs text-sm text-white/70">
                        Turn on your camera to start recording your video introduction.
                      </p>
                    </div>
                  )}

                  {isRecording && (
                    <div className="absolute left-4 top-4 flex items-center gap-2 rounded-full bg-black/60 px-3 py-1.5 text-sm font-medium text-white">
                      <span className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
                      {formatRecordingTime(recordingSeconds)} / {formatRecordingTime(MAX_RECORDING_SECONDS)}
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-center gap-3 bg-gray-50 px-5 py-4">
                  {!isCameraActive && (
                    <Button type="button" onClick={onStartCamera}>
                      <Camera className="mr-2 h-4 w-4" />
                      Turn On Camera
                    </Button>
                  )}
                  {isCameraActive && !isRecording && (
                    <Button type="button" onClick={onStartRecording}>
                      <span className="mr-2 h-2.5 w-2.5 rounded-full bg-white" />
                      Start Recording
                    </Button>
                  )}
                  {isCameraActive && isRecording && (
                    <Button type="button" variant="destructive" onClick={onStopRecording}>
                      <Square className="mr-2 h-4 w-4" />
                      Stop Recording
                    </Button>
                  )}
                </div>
              </div>
            )}

            {cameraError && (
              <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{cameraError}</span>
              </div>
            )}
          </div>
        )}

        {videoError && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{videoError}</span>
          </div>
        )}

        <div className="mt-8 flex items-center justify-between gap-3 border-t pt-5">
          <Button type="button" variant="outline" disabled={isSubmitting} onClick={onBack}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Previous
          </Button>
          <Button type="button" disabled={isSubmitting} onClick={onSubmit}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                Submit Application
                <CheckCircle2 className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}